import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Callout } from '@/components/ui/callout'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useFinishAdSignIn } from '@/hooks/useAds'

/**
 * Where Google's sign-in screen sends the person back (docs/ad-analytics.plan.md,
 * phase 2). It hands the one-time code to the API as the signed-in person, then
 * moves on to the Ads page with what the sign-in found.
 *
 * The code works once, so it is sent once: a second send — React's development
 * double effect, or a reload of this page — would be refused by Google and read
 * as a failed sign-in.
 */
export function AdsGoogleReturnPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const finish = useFinishAdSignIn()
  const sent = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const code = params.get('code')
  const state = params.get('state')
  const declined = params.get('error')

  useEffect(() => {
    if (sent.current || declined !== null || code === null || state === null) return
    sent.current = true
    finish.mutate(
      { code, state },
      {
        onSuccess: (done) => void navigate('/ads', { replace: true, state: { signedIn: done } }),
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }, [code, state, declined, finish, navigate])

  if (declined !== null) {
    return (
      <PageContainer width="wide">
        <Callout tone="attention" title="Google Ads wasn’t connected">
          The sign-in was stopped before it finished. <Link to="/ads" className="font-medium text-ink underline underline-offset-2">Back to Ads</Link>
        </Callout>
      </PageContainer>
    )
  }
  if (code === null || state === null) {
    return (
      <PageContainer width="wide">
        <ErrorState message="This page is only reached from Google’s sign-in. Start again from Ads." />
      </PageContainer>
    )
  }
  if (error !== null) {
    return (
      <PageContainer width="wide" className="space-y-4">
        <ErrorState message={error} />
        <p className="m-0 text-center text-sm">
          <Link to="/ads" className="font-medium text-ink underline underline-offset-2">
            Back to Ads
          </Link>
        </p>
      </PageContainer>
    )
  }
  return (
    <PageContainer width="wide">
      <DetailSkeleton />
    </PageContainer>
  )
}
